<template>
  <div id="AccountUsers" class="Main-Content">

    <LoadingLayout v-if="loading" :page="true" />

    <Page
      v-else
      :title="$t('Users')"
      :secondaryActions="[{
        label: $t('Notes'),
        onAction: () => eventHub.$emit('showAccountNotes', 'USER')
      }]"
      size="fullWidth"
    >

      <Sheet
        :title="$t('AccountUsers.addNewUser')"
        :width="700"
        :visible="!!addUser"
        @close="cancelCreateUser"
        padded
      >
        <template v-slot:actions>
          <Button type="primary" :disabled="saving" @click="createUser">{{$t('AccountUsers.addUser')}}</Button>
        </template>
        <Card>
          <CardSection>
              
            <FormLayout>
              <TextContainer>
                <div>{{$t('AccountUsers.addUserHelp')}}</div>
              </TextContainer>

              <div v-if="addUserErrors.other" class="error2">{{ $t(addUserErrors.other) }}</div>
              <TextField
                :label="$t('Email')"
                v-model.trim="newUser.email"
                :error="addUserErrors.email ? $t(addUserErrors.email) : null"
                ref="createNewUserEmailField"
              />
              <TextField
                :label="$t('First Name')"
                v-model.trim="newUser.firstName"
                :error="addUserErrors.firstName ? $t(addUserErrors.firstName) : null"
              />
              <TextField
                :label="$t('Last Name')"
                v-model.trim="newUser.lastName"
                :error="addUserErrors.lastName ? $t(addUserErrors.lastName) : null"
              />

              <Select
                :label="$t('Language')"
                :options="newUserLanguages"
                v-model="newUser.defaultLanguage"
              />
            </FormLayout>

          </CardSection>
        </Card>
      </Sheet>

      <Sheet
        :title="$t('AccountUsers.addNewUser')"
        :width="700"
        :visible="!!addAgencyUser"
        @close="cancelCreateAgencyUser"
        padded
      >
        <template v-slot:actions>
          <Button type="primary" @click="createAgencyUser">{{$t('AccountUsers.addAgencyUser')}}</Button>
        </template>
        <Card>
          <CardSection>

            <FormLayout>
              <TextContainer>
                <div>{{$t('AccountUsers.emailAddressHelp')}}</div>
              </TextContainer>

              <TextField
                :label="$t('Email')"
                v-model="newAgencyUser.email"
                :error="addAgencyErrors.email"
                ref="createNewUserEmailField"
              />
            </FormLayout>

          </CardSection>
        </Card>
      </Sheet>

      <Sheet
        :title="$t('AccountUsers.editAccess')"
        :width="500"
        :visible="!!editUser"
        @close="cancelEditUser"
        padded
      >
        <template v-slot:actions>
          <Button type="primary" @click="updateUser" :disabled="!canUpdateUser || saving">{{$t('AccountUsers.updatePermissions')}}</Button>
        </template>
        <template v-if="editUser">
          <Card :title="$t('AccountUsers.generalAccess')">
            <CardSection>
              <ChoiceList
                :options="accessOptions"
                multiple
                v-model="editUserAccess"
              />
            </CardSection>
          </Card>
          <Card 
            :title="$t('AccountUsers.insightsDashboardAccess')" 
            :actions="[{
              label: $t('Toggle All'),
              disabled: editUserIsAdmin,
              onAction: toggleDashboards
            }]"
          >
            <CardSection>
              <!-- <Button @click="toggleDashboards" size="small" :disabled="editUserIsAdmin">{{$t('Toggle All')}}</Button> -->
              <ChoiceList
                :options="dashboardsForAccount"
                :disabled="editUserIsAdmin"
                multiple
                v-model="editUserInsights"
              />
            </CardSection>
          </Card>
        </template>
      </Sheet>

      <Layout>
        <LayoutSection>
          <Card
            :title="$t('Users')"
            :actions="[
              {
                label: $t('AccountUsers.addUser'),
                onAction: () => showCreateUser()
              }
            ]"
          >
            <DataTable
              :columns="[
                { key: 'firstName', label: $t('Name'), width: 175, fixed: true},
                { key: 'email', label: $t('Email')},
                { key: 'access', label: $t('AccountUsers.generalAccess'), width: '350px'},
                { key: 'dashboards', label: $t('AccountUsers.insightsDashboards')}
              ]"
              :rows="users"
              :row-actions="row => getRowActions(row, 'user')"
              compact
            >
              <template v-slot:col:firstName="{row}">
                <div v-if="isLocked(row)" class="lockedLock"><Icon icon="fas fa-lock fa-lg" /></div>
                <div>{{row.firstName}} {{row.lastName}}</div>
                <div v-if="isLocked(row)" class="lockedAction"><a href="#" @click="unlock(row)">{{ $t('Unlock') }}</a></div>
              </template>
              <template v-slot:col:access="{row}">
                <table class="table table-bordered">
                  <tr>
                    <td v-for="(type, index) in Object.keys(accessMap)" :key="index" class="access-cell title">{{accessMap[type]}}</td>
                  </tr>
                  <tr>
                    <td v-for="(type, index) in Object.keys(accessMap)" :key="index" class="access-cell data">
                      <Icon v-if="!!row[type]" icon="fas fa-check-circle" class="active-access"/>
                      <Icon v-else icon="far fa-circle" class="no-access"/>
                    </td>
                  </tr>
                </table>
              </template>
              <template v-slot:col:dashboards="{row}">
                {{getInsightsDashboardsLength(row)}} / {{totalDashboardsLength}}
              </template>
            </DataTable>

          </Card>

          <template v-if="agencyUsers && agencyUsers.length">

            <Card
              :title="$t('Agency Users')"
              :actions="[
                {
                  label: $t('AccountUsers.addAgencyUser'),
                  onAction: () => showCreateAgencyUser()
                }
              ]"
            >           
              <DataTable
                :columns="[
                  { key: 'firstName', label: $t('Name')},
                  { key: 'email', label: $t('Email')},
                  { key: 'access', label: $t('AccountUsers.generalAccess'), maxWidth: '250px', wrap:true},
                  { key: 'dashboards', label: $t('AccountUsers.insightsDashboards')}
                ]"
                :rows="agencyUsers"
                :row-actions="row => getRowActions(row, 'agent')"
                compact
              >
                <template v-slot:col:firstName="{row}">
                  <div>{{row.firstName}} {{row.lastName}}</div>
                </template>
                <template v-slot:col:access="{row}">
                  <table class="table table-bordered">
                    <tr>
                      <td v-for="(type, index) in Object.keys(accessMap)" :key="index" class="access-cell title">{{accessMap[type]}}</td>
                    </tr>
                    <tr>
                      <td v-for="(type, index) in Object.keys(accessMap)" :key="index" class="access-cell data">
                        <Icon v-if="!!row[type]" icon="fas fa-check-circle" class="active-access"/>
                        <Icon v-else icon="far fa-circle" class="no-access"/>
                      </td>
                    </tr>
                  </table>
                </template>
                <template v-slot:col:dashboards="{row}">
                  {{getInsightsDashboardsLength(row)}} / {{totalDashboardsLength}}
                </template>
              </DataTable>
            </Card>

          </template><!-- has agency users -->

        </LayoutSection>
      </Layout>

    </Page>

  </div>
</template>

<script>

import _ from 'lodash'

export default {
  name: 'AccountUsers',
  data () {
    return {
      addUser: false,
      newUser: {
        email: null,
        firstName: null,
        lastName: null,
        defaultLanguage: 'en'
      },
      addUserErrors: {},

      loading: true,
      saving: false,
      users: [],

      agencyUsers: [],
      agencyLink: null,
      addAgencyUser: false,
      newAgencyUser: {
        email: null
      },
      addAgencyErrors: [],
      dashboardsForAccount: [],

      editUser: null,
      editUserAccessCopy: [],
      editUserInsightsCopy: [],
      editUserAccess: [],
      editUserInsights: [],
      editType: null,
      accessTypes: [
        'isAdmin',
        'hasCampaignAccess',
        'hasBuilderAccess',
        'hasTransactionAccess',
        'hasInvoiceAccess',
        'hasConversionDefinitionAccess',
        'hasNotificationAccess',
        'hasRecommendationsAccess',
        'hasSmsAccess',
        'insightsDashboards'
      ]
    }
  },
  mounted() {
    this.newUser.defaultLanguage = this.theUser && this.theUser.defaultLanguage || 'en'

    NProgress.start()
    Promise.all([
      this.api.getUsersByAccountId(),
      this.api.getAgencyUsersByAccountId(),
      this.api.getInsightsDashboardsForAccount()
    ])
      .then((response) => {
        let [users, agencyUsers, dashboardsForAccount] = response
        this.users = users

        this.agencyUsers = agencyUsers

        this.dashboardsForAccount = dashboardsForAccount.map(d => ({
          value: d.id,
          label: this.$t(`AccountUsers.${d.name}`)
        }))

        this.$watch('editUserAccess', (newVal, oldVal) => {
          if (this.editType && !oldVal.find(v => v === 'isAdmin') && newVal.find(v => v === 'isAdmin')) {
            this.editUserAccess = this.accessOptions.map(d => d.value)
            this.editUserInsights = this.dashboardsForAccount.map(d => d.value)
          }
        })

        this.loading = false
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        NProgress.done()
      })
  },
  computed: {
    newUserLanguages() {
      return this.supportedLanguages
    },
    totalDashboardsLength() {
      return this.dashboardsForAccount.length
    },
    editUserIsAdmin() {
      return this.editUserAccess.find(a => a === 'isAdmin')
    },
    canUpdateUser() {
      return  !(_.isEqual(_.sortBy(this.editUserAccess), _.sortBy(this.editUserAccessCopy)) 
              && _.isEqual(_.sortBy(this.editUserInsights), _.sortBy(this.editUserInsightsCopy)))
    },
    accessOptions() {
      const opts = [
        {
          label: this.$t('Admin'),
          value: 'isAdmin',
          disabled: this.editUser.id === this.theUser.userId,
          helpText: this.$t('AccountUsers.adminAccessMessage')
        },
        {
          label: this.$t('Campaigns'),
          value: 'hasCampaignAccess',
          disabled: this.editUserIsAdmin,
          helpText: this.$t('AccountUsers.campaignAccessMessage')
        },
        {
          label: this.$t('Creative'),
          value: 'hasBuilderAccess',
          disabled: this.editUserIsAdmin,
          helpText: this.$t('AccountUsers.creativeAccessMessage')
        },
        {
          label: this.$t('Conversions'),
          value: 'hasTransactionAccess',
          disabled: this.editUserIsAdmin,
          helpText: this.$t('AccountUsers.transactionAccessMessage')
        },
        {
          label: this.$t('Invoices'),
          value: 'hasInvoiceAccess',
          disabled: this.editUserIsAdmin,
          helpText: this.$t('AccountUsers.invoiceAccessMessage')
        },
        {
          label: this.$t('Conversion Definitions'),
          value: 'hasConversionDefinitionAccess',
          disabled: this.editUserIsAdmin,
          helpText: this.$t('AccountUsers.conversionDefinitionAccessMessage')
        },
      ]

      if (this.theUser.webPushEnabled) {
        opts.push ({
          label: this.$t('Notifications'),
          value: 'hasNotificationAccess',
          disabled: this.editUserIsAdmin,
          helpText: this.$t('AccountUsers.notificationAccessMessage')
        })
      }

      if (this.theUser.smsEnabled) {
        opts.push ({
          label: this.$t('SMS'),
          value: 'hasSmsAccess',
          disabled: this.editUserIsAdmin,
          helpText: this.$t('AccountUsers.smsAccessMessage')
        })
      }

      if (this.theUser.recommendationsEnabled) {
        opts.push ({
          label: this.$t('Recommendations'),
          value: 'hasRecommendationsAccess',
          disabled: this.editUserIsAdmin,
          helpText: this.$t('AccountUsers.recommendationsAccessMessage')
        })
      }

      return opts
    },
    accessMap() {
      const map = {
        'isAdmin': this.$t('Admin'),
        'hasCampaignAccess': this.$t('Campaigns'),
        'hasBuilderAccess': this.$t('Creative'),
        'hasTransactionAccess': this.$t('Transactions'),
        'hasInvoiceAccess': this.$t('Invoices'),
        'hasConversionDefinitionAccess': this.$t('Conversion Definitions')
      }
      if (this.theUser.webPushEnabled) {
        map['hasNotificationAccess'] = this.$t('Notifications')
      }
      if (this.theUser.recommendationsEnabled) {
        map['hasRecommendationsAccess'] = this.$t('Recommendations')
      }
      if (this.theUser.smsEnabled) {
        map['hasSmsAccess'] = this.$t('SMS')
      }
      return map
    }
  },
  methods: {
    isLocked(user) {
      return user.isLockedFailedLogin || user.isLockedInactivity
    },
    async unlock(user) {
      const unlock = await this.$ui.confirm({
        title: this.$t('Unlock User?'),
        content: this.$t('The user has been locked. Would you like to unlock the users access to this account?')
      })
      if (unlock) {
        this.api.unlockUser(user.id)
        .then(() => {
          user.isLockedFailedLogin = false
          user.isLockedInactivity = false
          this.$ui.toast({
            title: this.$t('User unlocked')
          })
        })
        .catch(this.apiCatch)
      }
    },
    cancelEditUser() {
      this.resetEditUser()
    },
    resetEditUser() {
      this.editUser = null
      this.editType = null
      this.editUserAccess = []
      this.editUserAccessCopy = []
      this.editUserInsights = []
      this.editUserInsightsCopy = []
    },
    setEditUserAccess() {
      const self = this
      this.accessTypes.forEach(a => {
        if (self.editUser[a]) {
          self.editUserAccess.push(a)
          self.editUserAccessCopy.push(a)
        }
      })
    },
    setEditUserInsights() {
      const self = this
      this.dashboardsForAccount.forEach(d => {
        self.editUser.insightsDashboards = self.editUser.insightsDashboards || ''
        if (self.editUser.insightsDashboards.split(',').find(i => i === d.value)) {
          self.editUserInsights.push(d.value)
          self.editUserInsightsCopy.push(d.value)
        }
      })
    },
    getRowActions(user, type) {
      const self = this
      const rv = [
        {
          label: this.$t('AccountUsers.editAccess'),
          disabled: this.saving,
          onAction() {
            self.editUser = _.cloneDeep(user)
            self.setEditUserAccess()
            self.setEditUserInsights()
            self.editType = type
          }
        }
      ]
      if (type === 'user') {
        rv.push({
          label: this.$t('Logout'),
          disabled: (user.id === self.theUser.userId) || this.saving,
          onAction() {
            self.logoutUser(user)
          }
        })
      }
      rv.push({
        label: this.$t('Remove'),
        disabled: (user.id === self.theUser.userId) || this.saving,
        onAction() {
          self.removeUser(user, type)
        }
      })
      return rv
    },
    async logoutUser(user) {
      const logout = await this.$ui.confirm({
        title: this.$t('AccountUsers.logoutUser'),
        content: this.$t(`AccountUsers.logoutUserMessage`, { value: `${user.firstName} ${user.lastName}` })
      })
      if (logout) {
        this.saving = true
        NProgress.start()
        this.api.logoutOtherUserSessions(user.id)
          .then(() => {
            this.$ui.toast({
              title: this.$t('Complete')
            })
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.saving = false
            NProgress.done()
          })
      }

    },
    getInsightsDashboardsLength(user) {
      if (user.insightsDashboards && user.insightsDashboards.split(',').length && user.insightsDashboards.split(',')[0]) {
        const userDashboardsMerged = user.insightsDashboards.split(',').filter(d => {
          return !!this.dashboardsForAccount.find(ad => ad.value === d)
        })
        return userDashboardsMerged.length
      }
      return 0
    },
    getChangedPermissions() {
      const changed = {}
      changed[this.editUser.id] = {}
      let user = null
      if (this.editType === 'user') {
        user = this.users.find(u => u.id === this.editUser.id)
      } else if (this.editType === 'agent') {
        user = this.agencyUsers.find(u => u.id === this.editUser.id)
      }
      for (const field of this.accessTypes) {
        if (field === 'insightsDashboards') {
          changed[this.editUser.id][field] = this.editUserInsights.join(',')
          user[field] = this.editUserInsights.join(',')
        } else if (this.editUserAccess.find(a => a === field)) {
          changed[this.editUser.id][field] = 1
          user[field] = 1
        } else {
          changed[this.editUser.id][field] = 0
          user[field] = 0
        }
      }
      return changed
    },
    updateUser() {
      if (this.editType === 'agent') {
        this.updateAgencyUser()
      } else if (this.editType === 'user') {
        this.updateBaseUser()
      }
    },
    updateBaseUser() {
      const changed = this.getChangedPermissions()
      // console.log(changed)
      NProgress.start()
      this.saving = true
      this.api.updateUserPermissions(changed)
        .then(() => {
          this.$ui.toast({ title: this.$t('AccountUsers.permissionsUpdated') })
          this.resetEditUser()
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },
    updateAgencyUser() {
      const changed = this.getChangedPermissions()
      NProgress.start()
      this.saving = true
      this.api.updateUserPermissions(changed, true)
      .then(() => {
        this.$ui.toast({ title: this.$t('AccountUsers.permissionsUpdated') })
        this.resetEditUser()
      })
      .catch(this.apiCatch)
      .finally(() => {
        this.saving = false
        NProgress.done()
      })
    },
    createAgencyUser() {
      this.addAgencyError = ''
      NProgress.start()
      this.saving = true
      this.api.createAgencyUser(this.newAgencyUser.email)
        .then((newUser) => {
          this.agencyUsers.unshift(Object.assign({}, newUser))
          this.cancelCreateAgencyUser()
          this.$ui.toast({ title: this.$t('AccountUsers.agencyUserAdded') })
        })
        .catch((e) => {
          this.apiCatch(e)
        })
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },
    cancelCreateAgencyUser() {
      this.addAgencyUser = false
      this.newAgencyUser.email = ''
    },
    showCreateUser() {
      this.addUser = true
      // this.$nextTick(() => this.$refs.createNewUserEmailField.focus())
    },
    showCreateAgencyUser() {
      this.addAgencyUser = true
    },
    createUser() {
      this.addUserErrors = {}
      NProgress.start()
      this.saving = true
      this.api.createNewUser(this.newUser)
        .then((newUser) => {
          this.users.unshift(Object.assign({}, newUser))
          this.cancelCreateUser()
          this.$ui.toast({ title: this.$t('AccountUsers.userAdded') })
        })
        .catch((e) => {
          this.apiCatch(e)
        })
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },
    cancelCreateUser() {
      this.addUser = false
      this.newUser.email = ''
      this.newUser.firstName = ''
      this.newUser.lastName = ''
      this.newUser.defaultLanguage = this.theUser && this.theUser.defaultLanguage || 'en'
      this.addUserErrors = {}
    },
    async removeUser(user, type) {
      if (!await this.$ui.confirm({ title: this.$t('AccountUsers.removeUserMessage', [`${user.firstName}`, `${user.lastName}`]) })) {
        return
      }
      NProgress.start()
      this.saving = true
      this.api.removeUser(user.id, type)
        .then(() => {
          if (type === 'user') { 
            this.users.splice(this.users.findIndex(u => u.email === user.email), 1)
          } else if (type === 'agent') {
            this.agencyUsers.splice(this.agencyUsers.findIndex(u => u.email === user.email), 1)
          }
          // this.oldUser.splice(this.oldUser.findIndex(u => u.email === user.email), 1)
          this.$ui.toast({ title: this.$t('AccountUsers.userRemoved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },
    userDashboardsLength(dashboards) {
      if (!dashboards) {
        return 0
      }
      let a = dashboards.split(',')
      if (a[0] === '') {
        a.shift()
      }
      return a.length
    },
    toggleDashboards() {
      if (this.editUserInsights.length !== this.dashboardsForAccount.length) {
        this.editUserInsights = this.dashboardsForAccount.map(d => d.value)
      } else {
        this.editUserInsights = []
      }
    },
  },
  components: {
  }
}

</script>

<style lang="scss" scoped>
#AccountUsers {
  .access-cell {
    padding: 5px;
    white-space: nowrap;
    text-align: center;
    &.title {
      border: none;
      border-right: 1px solid #C4CDD5;
      padding-top: 3px;
    }
    &.title:last-of-type {
      border-right: none;
    }
    &.data {
      border: none;
      border-right: 1px solid #C4CDD5;
      border-top: 1px solid #C4CDD5;
      padding-bottom: 3px;
    }
    &.data:last-of-type {
      border-right: none;
    }
    .active-access {
      color: #007AFF;
    }
    .no-access {
      color: #DFE3E9;
    }
  }

  .alert {
    margin-top: 15px;
  }

  .fa-check-square {
    color: #5CB85C;
  }
  .fa-plus-circle {
    color: #5CB85C;
  }
  .dashboard-info {
    margin-top: 4px;
    font-size: 12px;
    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
    a {
      display: inline-block;
      margin-left: 5px;
    }
  }
}
.lockedLock {
  color:#d80128;
  float: left;
  margin-right: 5px;
}
.lockedAction {
  padding-top: 5px;
  text-decoration: underline;
}
.error2 {
  color: #c00;
  margin-top: 15px;
  font-weight: bold;
}
</style>